import React, { useEffect, useState, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { initializeApp, getApps } from 'firebase/app'
import { getDatabase, ref, onValue, off } from 'firebase/database'
import { getAuth, onAuthStateChanged } from 'firebase/auth'

// ─── Firebase ─────────────────────────────────────────────────────────────────
const FIREBASE_CONFIG = {
    apiKey:            'AIzaSyD10olyURDLvM2BdtkLTnwVVB98qfNiDcc',
    authDomain:        'clubswap-5a74a.firebaseapp.com',
    databaseURL:       'https://clubswap-5a74a-default-rtdb.firebaseio.com',
    projectId:         'clubswap-5a74a',
    storageBucket:     'clubswap-5a74a.firebasestorage.app',
    messagingSenderId: '274838532963',
    appId:             '1:274838532963:web:4044ba76f6192dcfdf5956',
}

let _app, _db
function getDB() {
    if (!_db) {
        _app = getApps().length ? getApps()[0] : initializeApp(FIREBASE_CONFIG)
        _db  = getDatabase(_app)
    }
    return _db
}

// ─── Nav config ───────────────────────────────────────────────────────────────
// Each item has a unique `id` used for active detection — NOT just path matching
const DESKTOP_NAV = [
    { id: 'home',        label: 'Home',        icon: '⌂',  path: '/',            activeOn: ['/'] },
    { id: 'challenge',   label: 'Challenge',   icon: '⚔',  path: '/',            activeOn: ['/challenge'] },
    { id: 'tournaments', label: 'Tournaments', icon: '🏆', path: '/tournaments', activeOn: ['/tournaments', '/tournament'] },
    { id: 'deposit',     label: 'Deposit',     icon: '💎', path: '/account',     activeOn: ['/account'] },
]

const MOBILE_NAV = [
    { id: 'home',        label: 'Home',        icon: '⌂',  path: '/',            activeOn: ['/'] },
    { id: 'challenge',   label: 'Challenge',   icon: '⚔',  path: '/',            activeOn: [] },
    { id: 'tournaments', label: 'Tournaments', icon: '🏆', path: '/tournaments', activeOn: ['/tournaments', '/tournament'], center: true },
    { id: 'profile',     label: 'Profile',     icon: '◉',  path: '/account',     activeOn: ['/account'] },
]

// ─── Helpers ──────────────────────────────────────────────────────────────────
function initials(name) {
    if (!name) return '?'
    return name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2)
}
function fmt(n) { return Number(n || 0).toLocaleString() }

function isNavActive(item, path) {
    // Exact match for home
    if (item.id === 'home') return path === '/'
    // Never auto-highlight items with empty activeOn
    if (!item.activeOn || item.activeOn.length === 0) return false
    return item.activeOn.some(p => path === p || path.startsWith(p + '/'))
}

// ─── Global CSS injection (for media queries) ─────────────────────────────────
const GLOBAL_CSS = `
    /* Hide sidebar on mobile */
    @media (max-width: 768px) {
        .cs-sidebar { display: none !important; }
        .cs-main { margin-left: 0 !important; }
        .cs-topbar-logo { display: flex !important; }
        .cs-balance-pill { display: none !important; }
    }
    /* Hide bottom nav on desktop */
    @media (min-width: 769px) {
        .cs-bottom-nav { display: none !important; }
        .cs-topbar-logo { display: none !important; }
    }
    /* Nav hover */
    .cs-nav-btn:hover { background: rgba(74,222,128,0.06) !important; color: #a3e6b0 !important; }
    .cs-nav-btn:hover .cs-nav-icon { opacity: 0.9 !important; }
    /* Bottom nav btn hover */
    .cs-bottom-btn:hover span { color: #4ade80 !important; }
    /* Smooth sidebar */
    .cs-sidebar { transition: transform 0.2s ease; }
    /* Content area */
    .cs-content { min-height: calc(100vh - 56px); }
`

function injectCSS() {
    if (document.getElementById('cs-layout-css')) return
    const style = document.createElement('style')
    style.id = 'cs-layout-css'
    style.textContent = GLOBAL_CSS
    document.head.appendChild(style)
}

// ─────────────────────────────────────────────────────────────────────────────
//  LOGO SVG (club card suit with game elements)
// ─────────────────────────────────────────────────────────────────────────────
function ClubSwapLogo({ size = 32 }) {
    return (
        <img
            src="/ClubSwap-Logo.jpeg"
            alt="ClubSwap"
            style={{ width: size, height: size, borderRadius: 8, objectFit: 'cover', flexShrink: 0 }}
            onError={e => {
                // Fallback SVG if image not found
                e.target.style.display = 'none'
                e.target.nextSibling && (e.target.nextSibling.style.display = 'flex')
            }}
        />
    )
}

// ─────────────────────────────────────────────────────────────────────────────
//  MAIN LAYOUT
// ─────────────────────────────────────────────────────────────────────────────
export default function Layout({ children }) {
    const navigate = useNavigate()
    const location = useLocation()
    const path     = location.pathname

    const [currentUser, setCurrentUser] = useState(null)
    const [diamonds, setDiamonds]       = useState(0)
    const [displayName, setDisplayName] = useState('')
    const [unreadCount, setUnreadCount] = useState(0)

    // Inject global CSS once
    useEffect(() => { injectCSS() }, [])

    // ── Auth ──────────────────────────────────────────────────
    useEffect(() => {
        const db   = getDB()
        const auth = getAuth(getApps().length ? getApps()[0] : initializeApp(FIREBASE_CONFIG))
        const unsub = onAuthStateChanged(auth, user => {
            setCurrentUser(user)
            if (!user) { setDiamonds(0); setDisplayName(''); return }

            const profileRef = ref(db, `users/${user.uid}/profile`)
            const balRef     = ref(db, `users/${user.uid}/balance`)

            onValue(profileRef, snap => {
                setDisplayName(snap.val()?.displayName || user.displayName || user.email?.split('@')[0] || 'Player')
            })
            onValue(balRef, snap => {
                setDiamonds(snap.val()?.diamonds ?? 0)
            })
        })
        return () => unsub()
    }, [])

    // ── Notification count ────────────────────────────────────
    useEffect(() => {
        if (!currentUser) { setUnreadCount(0); return }
        const db     = getDB()
        const cutoff = Date.now() - 48 * 60 * 60 * 1000
        const chalRef = ref(db, 'challenges')
        const unsub = onValue(chalRef, snap => {
            if (!snap.exists()) { setUnreadCount(0); return }
            let count = 0
            Object.values(snap.val()).forEach(c => {
                const involved = c.challengerUid === currentUser.uid || c.opponentUid === currentUser.uid
                if (involved && (c.createdAt || 0) > cutoff) count++
            })
            setUnreadCount(count)
        })
        return () => off(chalRef)
    }, [currentUser])

    return (
        <div style={s.root}>

            {/* ════════ DESKTOP SIDEBAR ════════ */}
            <aside className="cs-sidebar" style={s.sidebar}>

                {/* Logo */}
                <div style={s.logoArea} onClick={() => navigate('/')}>
                    <ClubSwapLogo size={36} />
                    <div>
                        <div style={s.logoName}>ClubSwap</div>
                        <div style={s.logoSub}>Play · Compete · Earn</div>
                    </div>
                </div>

                {/* Nav */}
                <nav style={s.nav}>
                    {DESKTOP_NAV.map(item => {
                        const active = isNavActive(item, path)
                        return (
                            <button
                                key={item.id}
                                className="cs-nav-btn"
                                style={active ? s.navActive : s.navBtn}
                                onClick={() => navigate(item.path)}
                            >
                                <span className="cs-nav-icon" style={active ? s.navIconActive : s.navIcon}>
                                    {item.icon}
                                </span>
                                <span style={active ? s.navLabelActive : s.navLabel}>{item.label}</span>
                                {active && <div style={s.activeBar} />}
                            </button>
                        )
                    })}
                </nav>

                {/* User card */}
                <div style={s.sidebarBottom}>
                    <div style={s.divider} />
                    {currentUser ? (
                        <div style={s.userCard} onClick={() => navigate('/account')}>
                            <div style={s.userAvatar}>{initials(displayName)}</div>
                            <div style={s.userMeta}>
                                <div style={s.userName}>{displayName}</div>
                                <div style={s.userBal}>💎 {fmt(diamonds)}</div>
                            </div>
                            <div style={s.userArrow}>›</div>
                        </div>
                    ) : (
                        <button style={s.signInSidebar} onClick={() => navigate('/account')}>
                            Sign In →
                        </button>
                    )}
                </div>
            </aside>

            {/* ════════ RIGHT COLUMN ════════ */}
            <div className="cs-main" style={s.mainCol}>

                {/* Top bar */}
                <header style={s.topBar}>

                    {/* Mobile logo — hidden on desktop via CSS */}
                    <div
                        className="cs-topbar-logo"
                        style={{ display: 'none', alignItems: 'center', gap: 8, cursor: 'pointer' }}
                        onClick={() => navigate('/')}
                    >
                        <ClubSwapLogo size={26} />
                        <span style={s.mobileLogoText}>ClubSwap</span>
                    </div>

                    {/* Right side */}
                    <div style={s.topRight}>
                        {/* Balance pill — desktop shows it in topbar, mobile hides (shown in sidebar) */}
                        {currentUser && (
                            <div className="cs-balance-pill" style={s.balancePill}>
                                <span>💎</span>
                                <span style={s.balAmt}>{fmt(diamonds)}</span>
                            </div>
                        )}

                        {/* Bell */}
                        <button style={s.bellBtn} onClick={() => navigate('/notifications')} title="Notifications">
                            🔔
                            {unreadCount > 0 && (
                                <span style={s.bellBadge}>{unreadCount > 9 ? '9+' : unreadCount}</span>
                            )}
                        </button>

                        {/* Avatar → account */}
                        {currentUser ? (
                            <button style={s.avatarBtn} onClick={() => navigate('/account')} title={displayName}>
                                {initials(displayName)}
                            </button>
                        ) : (
                            <button style={s.signInTopBtn} onClick={() => navigate('/account')}>
                                Sign In
                            </button>
                        )}
                    </div>
                </header>

                {/* Page content */}
                <main className="cs-content" style={s.content}>
                    {children}
                </main>

                {/* ════════ MOBILE BOTTOM NAV ════════ */}
                <nav className="cs-bottom-nav" style={s.bottomNav}>
                    {MOBILE_NAV.map(item => {
                        const active = isNavActive(item, path)
                        if (item.center) {
                            return (
                                <button
                                    key={item.id}
                                    className="cs-bottom-btn"
                                    style={s.bottomCenter}
                                    onClick={() => navigate(item.path)}
                                >
                                    <div style={active ? s.centerBubbleOn : s.centerBubbleOff}>
                                        <span style={s.centerIcon}>{item.icon}</span>
                                        <span style={{ ...s.centerLabel, color: active ? '#4ade80' : '#6aaa6a' }}>
                                            {item.label}
                                        </span>
                                    </div>
                                </button>
                            )
                        }
                        return (
                            <button
                                key={item.id}
                                className="cs-bottom-btn"
                                style={s.bottomBtn}
                                onClick={() => navigate(item.path)}
                            >
                                <span style={{ fontSize: 20, color: active ? '#4ade80' : '#2a5a2c', filter: active ? 'drop-shadow(0 0 4px #4ade80)' : 'none' }}>
                                    {item.icon}
                                </span>
                                <span style={{ fontSize: 10, color: active ? '#4ade80' : '#2a5a2c', fontWeight: active ? 700 : 400, marginTop: 2 }}>
                                    {item.label}
                                </span>
                            </button>
                        )
                    })}
                </nav>
            </div>
        </div>
    )
}

// ─────────────────────────────────────────────────────────────────────────────
//  STYLES
// ─────────────────────────────────────────────────────────────────────────────
const SIDEBAR = 216
const TOPBAR  = 54
const BOTTOM  = 64

const s = {
    root: {
        display: 'flex',
        minHeight: '100vh',
        background: '#071309',
        color: '#d4ecd6',
        fontFamily: "'Segoe UI', 'SF Pro Display', system-ui, sans-serif",
    },

    // ── Sidebar ──────────────────────────────────────────────
    sidebar: {
        width: SIDEBAR,
        minHeight: '100vh',
        background: '#040d06',
        borderRight: '1px solid #0d2010',
        display: 'flex',
        flexDirection: 'column',
        position: 'fixed',
        top: 0,
        left: 0,
        bottom: 0,
        zIndex: 200,
        overflowY: 'auto',
    },

    logoArea: {
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '20px 18px 18px',
        borderBottom: '1px solid #0d2010',
        cursor: 'pointer',
        flexShrink: 0,
    },
    logoName: { fontSize: 15, fontWeight: 800, color: '#e8f5e9', letterSpacing: '0.02em' },
    logoSub:  { fontSize: 9,  color: '#2a5a2c', letterSpacing: '0.08em', marginTop: 1 },

    nav: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        padding: '10px 8px',
        gap: 1,
    },

    navBtn: {
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '10px 12px',
        borderRadius: 8,
        border: 'none',
        background: 'transparent',
        color: '#3a6a3c',
        fontSize: 13,
        fontWeight: 500,
        cursor: 'pointer',
        textAlign: 'left',
        width: '100%',
        position: 'relative',
        fontFamily: 'inherit',
    },
    navActive: {
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '10px 12px',
        borderRadius: 8,
        border: 'none',
        background: 'linear-gradient(135deg, #0c2a0e 0%, #0f3312 100%)',
        color: '#4ade80',
        fontSize: 13,
        fontWeight: 700,
        cursor: 'pointer',
        textAlign: 'left',
        width: '100%',
        position: 'relative',
        fontFamily: 'inherit',
        boxShadow: 'inset 0 0 0 1px rgba(74,222,128,0.15)',
    },
    navIcon:       { fontSize: 15, width: 18, textAlign: 'center', opacity: 0.4, flexShrink: 0 },
    navIconActive: { fontSize: 15, width: 18, textAlign: 'center', flexShrink: 0 },
    navLabel:      { flex: 1, color: '#3a6a3c' },
    navLabelActive:{ flex: 1, color: '#4ade80' },
    activeBar: {
        position: 'absolute',
        right: -8,
        top: '50%',
        transform: 'translateY(-50%)',
        width: 3,
        height: 20,
        background: '#4ade80',
        borderRadius: '2px 0 0 2px',
        boxShadow: '0 0 6px #4ade80',
    },

    sidebarBottom: { padding: '8px 8px 16px', flexShrink: 0 },
    divider: { height: 1, background: '#0d2010', margin: '0 4px 10px' },

    userCard: {
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '10px 12px',
        borderRadius: 10,
        background: '#0a1a0c',
        border: '1px solid #122514',
        cursor: 'pointer',
    },
    userAvatar: {
        width: 32,
        height: 32,
        borderRadius: '50%',
        background: 'linear-gradient(135deg, #1a4a1c, #2a6a2c)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 11,
        fontWeight: 800,
        color: '#4ade80',
        flexShrink: 0,
        border: '1px solid rgba(74,222,128,0.3)',
    },
    userMeta:  { flex: 1, minWidth: 0 },
    userName:  { fontSize: 12, fontWeight: 600, color: '#c8e8ca', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
    userBal:   { fontSize: 11, color: '#4ade80', marginTop: 1 },
    userArrow: { fontSize: 14, color: '#2a5a2c' },
    signInSidebar: {
        width: '100%',
        padding: '10px',
        borderRadius: 8,
        border: '1px solid #1a3a1c',
        background: 'transparent',
        color: '#4ade80',
        fontSize: 13,
        fontWeight: 700,
        cursor: 'pointer',
        fontFamily: 'inherit',
    },

    // ── Main column ───────────────────────────────────────────
    mainCol: {
        flex: 1,
        marginLeft: SIDEBAR,
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
    },

    // ── Top bar ───────────────────────────────────────────────
    topBar: {
        height: TOPBAR,
        background: '#040d06',
        borderBottom: '1px solid #0d2010',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        padding: '0 16px',
        position: 'sticky',
        top: 0,
        zIndex: 100,
        flexShrink: 0,
        gap: 10,
    },
    mobileLogoText: {
        fontSize: 15,
        fontWeight: 800,
        color: '#4ade80',
        letterSpacing: '0.02em',
    },
    topRight: {
        display: 'flex',
        alignItems: 'center',
        gap: 10,
    },
    balancePill: {
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        background: '#0a2a0c',
        border: '1px solid rgba(74,222,128,0.2)',
        borderRadius: 20,
        padding: '5px 14px',
        fontSize: 13,
        fontWeight: 700,
        color: '#4ade80',
    },
    balAmt: { color: '#4ade80' },
    bellBtn: {
        position: 'relative',
        width: 36,
        height: 36,
        borderRadius: '50%',
        background: '#0a1a0c',
        border: '1px solid #122514',
        fontSize: 15,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        color: '#4ade80',
    },
    bellBadge: {
        position: 'absolute',
        top: -3,
        right: -3,
        background: '#ef4444',
        color: '#fff',
        fontSize: 8,
        fontWeight: 800,
        borderRadius: '50%',
        width: 15,
        height: 15,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: '1.5px solid #040d06',
    },
    avatarBtn: {
        width: 34,
        height: 34,
        borderRadius: '50%',
        background: 'linear-gradient(135deg, #1a4a1c, #2a6a2c)',
        border: '2px solid rgba(74,222,128,0.4)',
        color: '#4ade80',
        fontSize: 11,
        fontWeight: 800,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: 'inherit',
        flexShrink: 0,
    },
    signInTopBtn: {
        padding: '6px 14px',
        borderRadius: 20,
        background: '#4ade80',
        border: 'none',
        color: '#040d06',
        fontSize: 12,
        fontWeight: 800,
        cursor: 'pointer',
        fontFamily: 'inherit',
    },

    // ── Content ───────────────────────────────────────────────
    content: {
        flex: 1,
        overflowY: 'auto',
        paddingBottom: BOTTOM + 8,
    },

    // ── Mobile bottom nav ─────────────────────────────────────
    bottomNav: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-around',
        background: '#040d06',
        borderTop: '1px solid #0d2010',
        height: BOTTOM,
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 150,
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
    },

    bottomBtn: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        flex: 1,
        gap: 3,
        padding: '6px 4px',
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        fontFamily: 'inherit',
    },
    bottomCenter: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        flex: 1,
        padding: '4px',
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        fontFamily: 'inherit',
        marginTop: -18,
    },
    centerBubbleOn: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 2,
        background: 'linear-gradient(135deg, #1a4a1c, #2a6a2c)',
        border: '2px solid #4ade80',
        borderRadius: 18,
        padding: '8px 14px',
        boxShadow: '0 -4px 20px rgba(74,222,128,0.25)',
    },
    centerBubbleOff: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 2,
        background: '#0a1a0c',
        border: '2px solid #1a3a1c',
        borderRadius: 18,
        padding: '8px 14px',
    },
    centerIcon:  { fontSize: 18, color: '#4ade80' },
    centerLabel: { fontSize: 9, fontWeight: 700, letterSpacing: '0.04em' },
}
